import { IMAGE_JPEG_QUALITY, IMAGE_MAX_EDGE } from './attachment-limits'

export function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/** The size that fits `width × height` inside `maxEdge` on its long edge, never enlarging. */
export function fitWithin(width: number, height: number, maxEdge = IMAGE_MAX_EDGE): { width: number; height: number } {
  const longEdge = Math.max(width, height)
  if (longEdge <= maxEdge || longEdge === 0) return { width, height }
  const scale = maxEdge / longEdge

  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

interface DecodedImage {
  source: CanvasImageSource
  width: number
  height: number
  release: () => void
}

export interface PrepareImageDeps {
  decode: (file: Blob) => Promise<DecodedImage>
  createCanvas: () => HTMLCanvasElement
}

function decodeWithImageElement(file: Blob): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () =>
      resolve({
        source: img,
        width: img.naturalWidth,
        height: img.naturalHeight,
        release: () => URL.revokeObjectURL(url),
      })
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('This picture could not be read'))
    }

    img.src = url
  })
}

const defaultDeps: PrepareImageDeps = {
  decode: decodeWithImageElement,
  createCanvas: () => document.createElement('canvas'),
}

/**
 * The picture as the model will see it: scaled to a long edge of at most 1568 px and encoded as JPEG at
 * 0.85, on a white ground (JPEG has no transparency, and black is what a transparent PNG would become).
 * A GIF is sent as is, since re-encoding would drop its animation.
 */
export async function prepareImage(file: File, deps: PrepareImageDeps = defaultDeps): Promise<string> {
  if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) return readFileAsDataUrl(file)

  const decoded = await deps.decode(file)

  try {
    const { width, height } = fitWithin(decoded.width, decoded.height)
    const canvas = deps.createCanvas()
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('This picture could not be resized')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(decoded.source, 0, 0, width, height)

    return canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY)
  } finally {
    decoded.release()
  }
}
